<template>
  <section class="league-showcase">
    <div class="showcase-shell">
      <div class="showcase-header">
        <p class="showcase-kicker">Browse By League</p>
        <h2>Jump straight into the competitions that matter most.</h2>
        <p class="showcase-copy">
          Explore league hubs with featured matches, upcoming fixtures, and standings.
        </p>
      </div>

      <CardRailSkeleton v-if="isLoading" />

      <div v-else-if="leagues.length" class="league-rail">
        <router-link
          v-for="league in leagues"
          :key="league.league_slug"
          :to="`/football/league/${league.league_slug}`"
          class="league-card"
        >
          <div class="card-top">
            <div class="league-logo-wrap">
              <img
                v-if="league.league_logo"
                :src="league.league_logo"
                :alt="`${league.league_name} logo`"
                class="league-logo"
              />
            </div>
            <span class="matches-pill">{{ league.upcoming_matches }} matches</span>
          </div>

          <div class="card-body">
            <h3>{{ league.league_name }}</h3>
            <p class="season-label">{{ league.season || "Current season" }}</p>
            <p class="fixture-label">
              {{ league.first_team }} vs {{ league.second_team }}
            </p>
          </div>
        </router-link>
      </div>

      <div class="showcase-actions">
        <router-link to="/leagues-we-cover" class="showcase-cta">
          Browse All Covered Leagues
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import CardRailSkeleton from "./CardRailSkeleton.vue";
import { fetchJsonWithCache } from "../services/apiCache";

export default {
  name: "LeagueShowcase",
  components: {
    CardRailSkeleton,
  },
  data() {
    return {
      leagues: [],
      isLoading: true,
    };
  },
  async mounted() {
    await this.fetchLeagues();
  },
  methods: {
    async fetchLeagues() {
      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/football/fetchLeagueShowcase`;
        this.leagues = await fetchJsonWithCache({
          key: "football:league-showcase",
          url: apiUrl,
          ttlMs: 30000,
        });
      } catch (error) {
        console.error("Failed to fetch league showcase:", error);
        this.leagues = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.league-showcase {
  width: 100%;
  padding: 2px 0 0;
}

.showcase-shell {
  width: 100%;
}

.showcase-header {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto 18px;
  text-align: center;
}

.showcase-kicker {
  margin: 0 0 8px;
  color: #718294;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.showcase-header h2 {
  margin: 0;
  color: #142b48;
  font-size: clamp(1.3rem, 2.5vw, 2rem);
  line-height: 1.18;
}

.showcase-copy {
  margin: 10px 0 0;
  color: #5d7083;
}

.league-rail {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(260px, 300px);
  gap: 18px;
  overflow-x: auto;
  padding: 6px max(16px, calc((100vw - 1180px) / 2)) 14px;
  scroll-snap-type: x proximity;
  box-sizing: border-box;
}

.league-rail::-webkit-scrollbar {
  height: 10px;
}

.league-rail::-webkit-scrollbar-thumb {
  background: #d6e0ea;
  border-radius: 999px;
}

.league-card {
  display: flex;
  flex-direction: column;
  min-height: 240px;
  padding: 20px;
  border: 1px solid #e2eaf2;
  border-radius: 26px;
  background:
    radial-gradient(circle at top right, rgba(240, 203, 109, 0.15), transparent 22%),
    radial-gradient(circle at bottom left, rgba(70, 133, 194, 0.1), transparent 22%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 18px 40px rgba(21, 41, 68, 0.08);
  text-decoration: none;
  scroll-snap-align: start;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.league-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 24px 48px rgba(21, 41, 68, 0.12);
  border-color: #cfdae6;
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.league-logo-wrap,
.matches-pill {
  display: inline-flex;
  align-items: center;
}

.league-logo-wrap {
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: #f3f7fb;
  border: 1px solid #e1e8ef;
  overflow: hidden;
}

.league-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.matches-pill {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: #edf8f1;
  color: #2f6447;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.card-body {
  margin: 18px 0;
}

.card-body h3 {
  margin: 0;
  color: #173252;
  font-size: 1.35rem;
  line-height: 1.14;
}

.season-label {
  margin: 10px 0 0;
  color: #6f7e8c;
  font-size: 0.9rem;
  font-weight: 700;
}

.fixture-label {
  margin: 18px 0 0;
  color: #18324f;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}

.showcase-actions {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding: 0 16px;
}

.showcase-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 24px;
  border: 1px solid #16314d;
  border-radius: 999px;
  background: #16314d;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.showcase-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(22, 49, 77, 0.18);
  background: #21486e;
}

@media (max-width: 760px) {
  .showcase-header {
    width: calc(100% - 24px);
  }

  .league-rail {
    grid-auto-columns: minmax(250px, 84vw);
    gap: 14px;
    padding: 4px 12px 12px;
  }

  .league-card {
    min-height: 220px;
    padding: 18px;
    border-radius: 22px;
  }

  .card-top {
    flex-wrap: wrap;
  }
}
</style>
